// libs
import { useCallback, useRef, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { EditorMode } from '@/components/ContentEditor/types';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
// hooks
import useNavigation from '@/hooks/useNavigation';
// utils
import routes from '@/pages/corporate/routes';
import {
  AssignmentType,
  EventSelectionInput,
  ScheduleDateAssignmentConfigInput,
} from '@/types/schema';
import { getErrorMessage } from '@/utils/api/response';

import { isSchduledDateError } from '../../components/Editor/config';
import { usePublishAssignmentMutation } from '../../graphql/mutations/generated/publishAssignment';
import { useUpdateAssignmentMutation } from '../../graphql/mutations/generated/updateAssignment';
import { AssignmentCreatorPageProps } from '../../type';
import { AdaptedAssignmentItemDueDates } from '../../types';
import { adaptDueDatesOnSubmit, ScheduleForEnum, ScheduleItem } from '../../utils';

const useAssignmentCreator = ({
  mode,
  assignmentId,
  isInModal,
  onSuccess,
}: AssignmentCreatorPageProps) => {
  const navigation = useNavigation();
  const { t } = useTranslation();
  const isDuplicating = mode === EditorMode.Duplicate;
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();
  const [submitRef, setSubmitRef] = useState<Function | null>();
  const [{ fetching: fetchingPublishAssignment }, publishAssignment] =
    usePublishAssignmentMutation();
  const [{ fetching: fetchingUpdateAssignment }, updateAssignment] = useUpdateAssignmentMutation();
  const scheduleDateConfig = useRef<ScheduleDateAssignmentConfigInput>();

  const [localFormData, setFormData] = useState<AssignmentType | undefined>();

  const onAssign = useCallback(
    (value: ScheduleForEnum, item: ScheduleItem) => {
      scheduleDateConfig.current = item.value;
      submitRef?.();
    },
    [submitRef],
  );

  const onSubmit = useCallback(
    async (assignment: any) => {
      const { dueDateConfig } = assignment;

      if (
        scheduleDateConfig.current &&
        isSchduledDateError(scheduleDateConfig.current, dueDateConfig)
      ) {
        showErrorSnackbar({ message: t('AssignmentCreator:ScheduleDateError') });
        return;
      }

      setFormData({ ...assignment, scheduleDateConfig: scheduleDateConfig.current });
    },
    [showErrorSnackbar, t],
  );

  const onDialogClose = useCallback(() => {
    setFormData(undefined);
  }, []);

  const onConfirmAssignmentSubmit = useCallback(async () => {
    if (!localFormData) {
      return;
    }

    const {
      assignerId,
      contentIds,
      dueDateConfig,
      userGroupSyncConfigs,
      userIds,
      name,
      collectionIds,
      userGroupIds,
      assignmentItemsDueDateConfig,
      notificationConfigurations,
      eventSelections,
      importedUserGroupIds,
      removedUserIds,
    } = localFormData;

    const assignmentItemsDueDateConfigInput = adaptDueDatesOnSubmit(
      assignmentItemsDueDateConfig as unknown as AdaptedAssignmentItemDueDates,
    );

    if (assignmentId && !isDuplicating) {
      const { data, error } = await updateAssignment({
        data: {
          assignmentId,
          assignerId,
          contentIds,
          dueDateConfig,
          userGroupSyncConfigs,
          userIds,
          scheduleDateConfig: scheduleDateConfig.current,
          name,
          collectionIds,
          userGroupIds,
          assignmentItemsDueDateConfig: assignmentItemsDueDateConfigInput,
          notificationConfigurations,
          eventSelections: eventSelections as EventSelectionInput[] | undefined,
        },
      });

      if (data?.updateAssignment.assignment.id) {
        showSnackbar({ message: t('AssignmentCreator:UpdateContentSuccess') });
        navigation.replace(routes.Assignments());
      } else if (getErrorMessage(error?.graphQLErrors)) {
        const errorMessage = getErrorMessage(error?.graphQLErrors) as string;
        showErrorSnackbar({ message: errorMessage });
      } else {
        showDefaultErrorSnackbar();
      }
    } else {
      const { data, error } = await publishAssignment({
        data: {
          assignerId,
          contentIds,
          dueDateConfig,
          userGroupSyncConfigs,
          userIds,
          scheduleDateConfig: scheduleDateConfig.current,
          name,
          collectionIds,
          userGroupIds,
          assignmentItemsDueDateConfig: assignmentItemsDueDateConfigInput,
          notificationConfigurations,
          importedUserGroupIds,
          removedUserIds,
          eventSelections: eventSelections as EventSelectionInput[] | undefined,
        },
      });

      if (data?.publishAssignment.assignment.id) {
        if (isInModal) {
          onSuccess?.(data?.publishAssignment.assignment.id);
        } else {
          showSnackbar({ message: t('AssignmentCreator:AssignContentSuccess') });
          navigation.replace(routes.Assignments());
        }
      } else if (getErrorMessage(error?.graphQLErrors)) {
        const errorMessage = getErrorMessage(error?.graphQLErrors) as string;
        showErrorSnackbar({ message: errorMessage });
      } else {
        showDefaultErrorSnackbar();
      }
    }
    onDialogClose();
  }, [
    localFormData,
    assignmentId,
    isDuplicating,
    onDialogClose,
    updateAssignment,
    isInModal,
    onSuccess,
    showSnackbar,
    t,
    navigation,
    showErrorSnackbar,
    showDefaultErrorSnackbar,
    publishAssignment,
  ]);

  return {
    fetching: fetchingPublishAssignment || fetchingUpdateAssignment,
    onSubmit,
    setSubmitRef,
    onAssign,
    localFormData,
    onConfirmAssignmentSubmit,
    onDialogClose,
  };
};

export default useAssignmentCreator;
